import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useInterview } from '@features/interview/context/InterviewContext';
import { useMedia } from '@features/media/hooks/useMedia';
import { VideoPreview } from '@features/media/components/VideoPreview';
import { Button, Spinner, Modal } from '@shared/components/ui/UI';
import {
  CameraOffIcon,
  AlertIcon
} from '@shared/components/icons';
import { ROUTES, MEDIA_STATES, INTERVIEW_PHASES } from '@shared/utils/constants';
import { getErrorMessage } from '@shared/utils/helpers';
import IqarxLogo from '@assets/svg/iqarx.svg';
import QicardLogo from '@assets/svg/Qi.svg';

const CameraCheckPage = () => {
  const navigate = useNavigate();
  const { state, actions } = useInterview();
  const {
    stream,
    mediaState,
    cameraEnabled,
    microphoneEnabled,
    devices,
    permissionStatus,
    browserSupport,
    requestMedia,
    toggleCamera,
    toggleMicrophone,
    switchCamera,
    switchMicrophone,
    getDevices,
  } = useMedia();

  const [isInitializing, setIsInitializing] = useState(false);
  const [showPermissionModal, setShowPermissionModal] = useState(false);
  const [permissionError, setPermissionError] = useState(null);
  const [hasRequestedPermission, setHasRequestedPermission] = useState(false);

  // Check browser support on mount (but don't request camera yet)
  useEffect(() => {
    if (!browserSupport.isSupported) {
      setPermissionError(getErrorMessage('BROWSER_NOT_SUPPORTED', 'ar'));
    }
  }, [browserSupport.isSupported]);

  // Handle manual camera permission request
  const handleRequestCamera = async () => {
    if (!browserSupport.isSupported) {
      setPermissionError(getErrorMessage('BROWSER_NOT_SUPPORTED', 'ar'));
      return;
    }

    setIsInitializing(true);
    setHasRequestedPermission(true);

    // Request media access
    const mediaStream = await requestMedia();

    if (!mediaStream) {
      setShowPermissionModal(true);
    }

    setIsInitializing(false);
  };

  // Handle media state changes
  useEffect(() => {
    if (mediaState === MEDIA_STATES.DENIED) {
      setPermissionError(getErrorMessage('CAMERA_PERMISSION_DENIED', 'ar'));
      setShowPermissionModal(true);
    } else if (mediaState === MEDIA_STATES.ERROR) {
      setPermissionError(state.error?.message || getErrorMessage('UNKNOWN_ERROR', 'ar'));
    }
  }, [mediaState, state.error]);

  const handleRetryPermission = async () => {
    setPermissionError(null);
    setShowPermissionModal(false);
    const mediaStream = await requestMedia();
    if (!mediaStream) {
      setShowPermissionModal(true);
    }
  };

  const handleStartInterview = () => {
    if (stream) {
      // Get candidateId from context OR sessionStorage (fallback)
      const candidateId = state.interviewData?.candidateId || sessionStorage.getItem('candidateId');

      // Skip LivePreviewPage - go directly to interview
      actions.setPhase(INTERVIEW_PHASES.INTERVIEW);
      actions.setInterviewData({
        interviewStartTime: Date.now(),
      });

      // Navigate with candidateId as URL parameter
      if (candidateId) {
        navigate(`${ROUTES.INTERVIEW}?candidate_id=${candidateId}`);
      } else {
        console.error('CameraCheck: No candidateId found');
        // Navigate back to proceed page if no candidateId
        navigate(ROUTES.PROCEED);
      }
    }
  };

  const isReady = stream && mediaState === MEDIA_STATES.ACTIVE;

  return (
    <div className="min-h-screen bg-surface-light" dir="rtl">
      {/* Header with logos */}
      <header className="p-4 flex items-center justify-between">
        {/* IQARX Logo */}
        <div className="flex items-center gap-3">
          <img
                          src={IqarxLogo}
                          alt="IQARX"
                          className="h-10 w-auto"
                          style={{ filter: 'brightness(0)' }}
                        />
        </div>

        {/* QiCard Logo */}
        <div className="flex items-center gap-3">
<img
                          src={QicardLogo}
                          alt="Qi"
                          className="h-10 w-auto"
                          style={{ filter: 'brightness(0)' }}
                        />        </div>
      </header>

      {/* Main content */}
      <main className="max-w-4xl mx-auto px-6 py-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-4"
        >
          <h1 className="text-2xl font-bold text-text-primary mb-2">
            فحص الكاميرا!
          </h1>
          <p className="text-text-secondary">
            دعنا نتأكد من أن الكاميرا الخاصة بك مفعلة للمتابعة
          </p>
        </motion.div>

        {/* Video preview */}
        <motion.div
          initial={{ opacity: 0, scale: 0.95 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.2 }}
          className="relative max-w-xl mx-auto"
        >
          {!hasRequestedPermission ? (
            <div className="aspect-video bg-gradient-to-br from-gray-800 to-gray-900 rounded-2xl flex items-center justify-center">
              <div className="text-center p-8">
                <CameraOffIcon size={64} className="mx-auto text-gray-400 mb-4" />
                <h3 className="text-xl font-semibold text-white mb-3">
                  السماح بالوصول للكاميرا
                </h3>
                <p className="text-gray-300 mb-6">
                  نحتاج إلى الوصول إلى الكاميرا والميكروفون لإجراء المقابلة
                </p>
                <Button
                  variant="primary"
                  size="lg"
                  onClick={handleRequestCamera}
                  disabled={!browserSupport.isSupported}
                >
                  السماح بالوصول
                </Button>
              </div>
            </div>
          ) : isInitializing ? (
            <div className="aspect-video bg-gray-900 rounded-2xl flex items-center justify-center">
              <div className="text-center">
                <Spinner size="lg" className="mx-auto mb-4" />
                <p className="text-white">
                  جاري تهيئة الكاميرا...
                </p>
              </div>
            </div>
          ) : (
            <VideoPreview
              stream={stream}
              isActive={cameraEnabled}
              showStatus={false}
              showControls={true}
              cameraEnabled={cameraEnabled}
              microphoneEnabled={microphoneEnabled}
              onToggleCamera={toggleCamera}
              onToggleMicrophone={toggleMicrophone}
              className="aspect-video shadow-strong"
            />
          )}

          {/* Device selectors */}
          {devices.cameras.length > 1 || devices.microphones.length > 1 ? (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.4 }}
              className="mt-4 grid grid-cols-2 gap-4"
            >
              {devices.cameras.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    الكاميرا
                  </label>
                  <select
                    onChange={(e) => switchCamera(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {devices.cameras.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Camera ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}

              {devices.microphones.length > 1 && (
                <div>
                  <label className="block text-sm font-medium text-text-secondary mb-2">
                    الميكروفون
                  </label>
                  <select
                    onChange={(e) => switchMicrophone(e.target.value)}
                    className="w-full px-4 py-2 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-primary-500"
                  >
                    {devices.microphones.map((device) => (
                      <option key={device.deviceId} value={device.deviceId}>
                        {device.label || `Microphone ${device.deviceId.slice(0, 8)}`}
                      </option>
                    ))}
                  </select>
                </div>
              )}
            </motion.div>
          ) : null}
        </motion.div>

        {/* Error message */}
        {permissionError && !showPermissionModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="max-w-md mx-auto mt-4 p-4 bg-red-50 border border-red-200 rounded-xl"
          >
            <div className="flex items-center gap-3">
              <AlertIcon className="text-red-500 flex-shrink-0" size={20} />
              <p className="text-red-700 text-sm">{permissionError}</p>
            </div>
            <Button
              variant="ghost"
              size="sm"
              onClick={handleRetryPermission}
              className="mt-3 text-red-600"
            >
              إعادة المحاولة
            </Button>
          </motion.div>
        )}

        {/* Start button (only show after permission granted) */}
        {hasRequestedPermission && isReady && (
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.6 }}
            className="flex justify-center mt-6"
          >
            <Button
              variant="primary"
              size="xl"
              onClick={handleStartInterview}
              className="min-w-[200px]"
            >
              إبدأ
            </Button>
          </motion.div>
        )}
      </main>

      {/* Permission Error Modal */}
      <Modal
        isOpen={showPermissionModal}
        onClose={() => setShowPermissionModal(false)}
        title="مطلوب إذن الكاميرا"
      >
        <div className="space-y-4">
          <div className="flex justify-center">
            <div className="w-20 h-20 bg-amber-100 rounded-full flex items-center justify-center">
              <CameraOffIcon className="text-amber-600" size={40} />
            </div>
          </div>

          <p className="text-center text-text-secondary">
            نحتاج إلى الوصول إلى الكاميرا والميكروفون لإجراء المقابلة. يرجى السماح بالوصول من إعدادات المتصفح.
          </p>

          <div className="bg-surface-muted p-4 rounded-xl">
            <h4 className="font-medium text-sm mb-2">
              كيفية السماح بالوصول:
            </h4>
            <ol className="text-sm text-text-secondary space-y-1 list-decimal list-inside">
              <li>انقر على أيقونة الكاميرا/القفل في شريط العنوان</li>
              <li>اختر "السماح" للكاميرا والميكروفون</li>
              <li>قم بتحديث الصفحة</li>
            </ol>
          </div>

          <div className="flex gap-3">
            <Button
              variant="secondary"
              className="flex-1"
              onClick={() => setShowPermissionModal(false)}
            >
              إلغاء
            </Button>
            <Button
              variant="primary"
              className="flex-1"
              onClick={handleRetryPermission}
            >
              إعادة المحاولة
            </Button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CameraCheckPage;
